/*
 * Wishlist hearts.
 *
 * Signed-in customers: the list lives on the customer in Shopify
 * (custom.wishlist / custom.wishlist_ids). Toggles go to the app proxy at
 * /apps/wishlist, which Shopify signs with the customer id, so the browser
 * never says who the customer is.
 * Guests: the list lives in localStorage (id + handle). After they sign in it
 * is merged into the account once and cleared. Every guest save opens a small
 * sign-in sheet; the sign-in link returns to the same page. Once the guest
 * closes it ("Not now", X or Esc), the rest of that visit gets a short toast
 * with a sign-in link instead.
 *
 * Sign-in funnel: guest saves, sheet views/clicks/closes, sign-ins that come
 * back from the sheet and wishlist page views are posted to
 * /apps/wishlist/event (D1 log in the iwa-wishlist Worker, no guest id).
 *
 * Config comes from #WishlistData (snippets/wishlist-data.liquid). Buttons are
 * [data-wishlist-btn] (snippets/wishlist-button.liquid); clicks are delegated,
 * and buttons added later (recommendations, search, quick view) are picked up
 * by a MutationObserver.
 */
(function () {
  'use strict';

  var dataEl = document.getElementById('WishlistData');
  if (!dataEl) return;
  var cfg;
  try {
    cfg = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }

  var LOCAL_KEY = 'iwa-wishlist';
  var LIVE_KEY = 'iwa-wishlist-live';
  var LIVE_TTL = 5 * 60 * 1000;
  var SIGNIN_KEY = 'iwa-wishlist-signin';
  var SIGNIN_PARAM = 'wl_signin';
  var SIGNIN_TTL = 2 * 60 * 60 * 1000;
  var SNOOZE_KEY = 'iwa-wishlist-signin-snooze';
  var max = cfg.max || 100;

  function store(kind) {
    try {
      return window[kind];
    } catch (e) {
      return null;
    }
  }
  function readJSON(kind, key, fallback) {
    var s = store(kind);
    if (!s) return fallback;
    try {
      var v = JSON.parse(s.getItem(key));
      return v == null ? fallback : v;
    } catch (e) {
      return fallback;
    }
  }
  function writeJSON(kind, key, value) {
    var s = store(kind);
    if (!s) return;
    try {
      if (value == null) s.removeItem(key);
      else s.setItem(key, JSON.stringify(value));
    } catch (e) {}
  }

  // Guest entries: [{ id: Number, h: 'handle' }], newest first.
  function readLocal() {
    var list = readJSON('localStorage', LOCAL_KEY, []);
    return Array.isArray(list)
      ? list.filter(function (x) {
          return x && x.id;
        })
      : [];
  }
  function writeLocal(list) {
    writeJSON('localStorage', LOCAL_KEY, list.length ? list.slice(0, max) : null);
  }

  var ids = [];
  if (cfg.customer) {
    ids = (cfg.ids || []).map(Number);
    // A page served from cache can be a step behind a toggle made a moment
    // ago; the last answer from the proxy wins for a few minutes.
    var live = readJSON('sessionStorage', LIVE_KEY, null);
    if (live && Array.isArray(live.ids) && Date.now() - live.t < LIVE_TTL) ids = live.ids.map(Number);
  } else {
    ids = readLocal().map(function (x) {
      return Number(x.id);
    });
  }

  function has(id) {
    return ids.indexOf(Number(id)) !== -1;
  }

  // ---------------------------------------------------------------------------
  // UI

  function syncButton(btn) {
    var on = has(btn.getAttribute('data-wishlist-id'));
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('data-wl-synced', '');
  }

  function syncAll() {
    var btns = document.querySelectorAll('[data-wishlist-btn]');
    for (var i = 0; i < btns.length; i++) syncButton(btns[i]);
    var counts = document.querySelectorAll('[data-wishlist-count]');
    for (var j = 0; j < counts.length; j++) {
      counts[j].textContent = ids.length;
      counts[j].classList.toggle('is-visible', ids.length > 0);
    }
  }

  var toastEl, toastTimer;
  function toast(html) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wishlist-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
      toastEl.addEventListener('click', function (evt) {
        if (evt.target.closest('[data-wl-signin-toast]')) markSignIn('toast');
      });
    }
    toastEl.innerHTML = html;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 4000);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // ---------------------------------------------------------------------------
  // Sign-in funnel events. Fire and forget; keepalive lets a click event
  // leave before the sign-in page takes over.

  function inApp() {
    var ua = navigator.userAgent || '';
    if (/Instagram/i.test(ua)) return 'ig';
    if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(ua)) return 'fb';
    if (/musical_ly|BytedanceWebview|TikTok/i.test(ua)) return 'tt';
    if (/; wv\)|Line\/|Snapchat|Pinterest/i.test(ua)) return 'other';
    return '';
  }

  function stat(event, context, n) {
    try {
      var body = {
        e: event,
        c: context || undefined,
        n: typeof n === 'number' ? n : undefined,
        l: (document.documentElement.lang || '').slice(0, 5),
        m: window.matchMedia && window.matchMedia('(max-width: 768px)').matches ? 1 : 0,
        i: inApp(),
      };
      fetch(cfg.proxy + '/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'same-origin',
        keepalive: true,
      }).catch(function () {});
    } catch (e) {}
  }

  function saveContext(btn) {
    if (btn.closest('[data-wishlist-page]')) return 'page';
    if (btn.closest('.grid__item, .grid-product')) return 'card';
    return 'pdp';
  }

  // Sign-in link that comes back to this page, marked so the return can be
  // counted (customer_authentication/login takes relative return_to only).
  function signInUrl(context) {
    var params = new URLSearchParams(location.search);
    params.set(SIGNIN_PARAM, context);
    var back = location.pathname + '?' + params.toString();
    return cfg.login_path + '?return_to=' + encodeURIComponent(back);
  }

  function markSignIn(context) {
    stat('prompt_click', context);
    writeJSON('localStorage', SIGNIN_KEY, { c: context, t: Date.now() });
  }

  // A small non-modal sheet (bottom of the screen on phones, bottom corner on
  // desktop). It stays until closed; another save while it is open only
  // refreshes it.
  var sheet, sheetContext;
  function signInSheet(context) {
    if (cfg.customer) return;
    // Closed once this visit: a toast with a sign-in link, no sheet. Shares
    // already get their own "link copied" toast, so they get nothing more.
    if (readJSON('sessionStorage', SNOOZE_KEY, false)) {
      if (context === 'share') return;
      toast(
        escapeHtml(context === 'save_all' ? cfg.text.saved_all : cfg.text.saved_guest) +
          ' <a href="' + escapeHtml(signInUrl('toast')) + '" data-wl-signin-toast>' + escapeHtml(cfg.text.sign_in) + '</a>'
      );
      stat('prompt_view', 'toast');
      return;
    }
    var title = context === 'share' ? cfg.text.signin_keep : context === 'save_all' ? cfg.text.saved_all : cfg.text.signin_saved;
    if (!sheet) {
      sheet = document.createElement('div');
      sheet.className = 'wishlist-signin';
      sheet.setAttribute('role', 'dialog');
      sheet.setAttribute('aria-labelledby', 'WishlistSigninTitle');
      sheet.innerHTML =
        '<button type="button" class="wishlist-signin__close" data-wl-signin-close aria-label="' + escapeHtml(cfg.text.close) + '">' +
        '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
        '<p class="wishlist-signin__title" id="WishlistSigninTitle" aria-live="polite">' +
        '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path fill="currentColor" d="M12 21s-7.5-4.6-9.6-9.2C.9 8.6 2.9 4.5 6.7 4.5c2.2 0 3.6 1.2 4.3 2.4h2c.7-1.2 2.1-2.4 4.3-2.4 3.8 0 5.8 4.1 4.3 7.3C19.5 16.4 12 21 12 21z"/></svg>' +
        '<span data-wl-signin-title></span></p>' +
        '<p class="wishlist-signin__text">' + escapeHtml(cfg.text.signin_text) + '</p>' +
        '<div class="wishlist-signin__actions">' +
        '<a class="btn btn--small wishlist-signin__cta" data-wl-signin-cta>' + escapeHtml(cfg.text.signin_cta) + '</a>' +
        '<button type="button" class="wishlist-signin__later" data-wl-signin-close>' + escapeHtml(cfg.text.not_now) + '</button>' +
        '</div>';
      document.body.appendChild(sheet);
      sheet.addEventListener('click', function (evt) {
        if (evt.target.closest('[data-wl-signin-cta]')) {
          markSignIn(sheetContext);
          return;
        }
        if (evt.target.closest('[data-wl-signin-close]')) closeSheet(true);
      });
      document.addEventListener('keydown', function (evt) {
        if (evt.key === 'Escape' && sheet.classList.contains('is-visible')) closeSheet(true);
      });
    }
    sheetContext = context;
    sheet.querySelector('[data-wl-signin-title]').textContent = title;
    sheet.querySelector('[data-wl-signin-cta]').setAttribute('href', signInUrl(context));
    if (!sheet.classList.contains('is-visible')) {
      if (toastEl) toastEl.classList.remove('is-visible');
      sheet.classList.add('is-visible');
      stat('prompt_view', context);
    }
  }

  function closeSheet(byUser) {
    if (!sheet) return;
    sheet.classList.remove('is-visible');
    if (!byUser) return;
    stat('prompt_close', sheetContext);
    writeJSON('sessionStorage', SNOOZE_KEY, true);
  }

  // Back from a sign-in that started at a prompt: count it once, then drop
  // the marker param so a reload or a shared URL doesn't count it again.
  function countSignInReturn() {
    var params = new URLSearchParams(location.search);
    var fromUrl = params.get(SIGNIN_PARAM);
    var mark = readJSON('localStorage', SIGNIN_KEY, null);
    if (fromUrl != null) {
      params.delete(SIGNIN_PARAM);
      var qs = params.toString();
      try {
        history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
      } catch (e) {}
    }
    if (!cfg.customer) return;
    var context = fromUrl || (mark && Date.now() - mark.t < SIGNIN_TTL ? mark.c : null);
    if (mark) writeJSON('localStorage', SIGNIN_KEY, null);
    if (context && /^[a-z_]{1,16}$/.test(context)) stat('signin_return', context);
  }

  // ---------------------------------------------------------------------------
  // Proxy calls run one at a time, so two quick taps can't overwrite each
  // other's read-modify-write on the server.

  var queue = Promise.resolve();
  function proxy(path, body) {
    var run = function () {
      return fetch(cfg.proxy + path, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'same-origin',
      }).then(function (res) {
        return res.json().then(
          function (data) {
            if (!res.ok) {
              var err = new Error(data && data.error ? data.error : 'http_' + res.status);
              err.status = res.status;
              throw err;
            }
            return data;
          },
          function () {
            var err = new Error('http_' + res.status);
            err.status = res.status;
            throw err;
          }
        );
      });
    };
    var p = queue.then(run, run);
    queue = p.catch(function () {});
    return p;
  }

  function acceptServer(list) {
    ids = (list || []).map(Number);
    writeJSON('sessionStorage', LIVE_KEY, { ids: ids, t: Date.now() });
    syncAll();
  }

  // ---------------------------------------------------------------------------
  // Toggle

  function track(on, btn) {
    if (!on) return;
    var id = Number(btn.getAttribute('data-wishlist-id'));
    var handle = btn.getAttribute('data-wishlist-handle') || '';
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'add_to_wishlist', wishlist_product_id: id, wishlist_product_handle: handle });
    // Signed-in saves reach Klaviyo from the server with the account email;
    // for guests Klaviyo's own cookie is the only link, so tell it here.
    if (!cfg.customer) {
      window._learnq = window._learnq || [];
      window._learnq.push(['track', 'Added to Wishlist', { ProductID: id, Handle: handle, Source: 'guest' }]);
    }
  }

  function toggle(btn) {
    var id = Number(btn.getAttribute('data-wishlist-id'));
    var handle = btn.getAttribute('data-wishlist-handle') || '';
    if (!id) return;
    var on = !has(id);
    var before = ids.slice();

    ids = ids.filter(function (x) {
      return x !== id;
    });
    if (on) ids.unshift(id);
    ids = ids.slice(0, max);
    syncAll();
    track(on, btn);
    if (!on) removeFromPage(id);

    if (!cfg.customer) {
      var list = readLocal().filter(function (x) {
        return Number(x.id) !== id;
      });
      if (on) list.unshift({ id: id, h: handle });
      writeLocal(list);
      if (on) {
        stat('guest_save', saveContext(btn), list.length);
        signInSheet('save');
      }
      return;
    }

    proxy('/toggle', { id: id, on: on })
      .then(function (data) {
        acceptServer(data.ids);
      })
      .catch(function (err) {
        // Session gone (signed out in another tab): keep the save locally.
        if (err.status === 401) {
          cfg.customer = false;
          var list = readLocal().filter(function (x) {
            return Number(x.id) !== id;
          });
          if (on) list.unshift({ id: id, h: handle });
          writeLocal(list);
          return;
        }
        ids = before;
        syncAll();
        toast(escapeHtml(cfg.text.error));
      });
  }

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest && evt.target.closest('[data-wishlist-btn]');
    if (!btn) return;
    // Hearts sit inside product cards; never let the tap follow the card link.
    evt.preventDefault();
    evt.stopPropagation();
    toggle(btn);
  });

  // ---------------------------------------------------------------------------
  // Guest list -> account, once, right after sign-in.

  function mergeGuestList() {
    var local = readLocal();
    if (!cfg.customer || !local.length) return Promise.resolve(false);
    return proxy('/merge', {
      ids: local.map(function (x) {
        return Number(x.id);
      }),
    }).then(
      function (data) {
        writeLocal([]);
        acceptServer(data.ids);
        return true;
      },
      function () {
        return false;
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Wishlist page (sections/main-wishlist.liquid)

  var page = document.querySelector('[data-wishlist-page]');
  var ownGrid = page && page.querySelector('[data-wishlist-own] [data-wishlist-grid]');
  var shareBtn = page && page.querySelector('[data-wishlist-share]');

  function ownCards() {
    return ownGrid ? ownGrid.querySelectorAll('.grid__item[data-product-id]') : [];
  }

  function updatePageState() {
    if (!page || page.classList.contains('is-shared')) return;
    var empty = !ownCards().length;
    page.classList.toggle('is-empty', empty);
    if (shareBtn) shareBtn.hidden = empty;
  }

  function removeFromPage(id) {
    if (!ownGrid || page.classList.contains('is-shared')) return;
    var card = ownGrid.querySelector('.grid__item[data-product-id="' + id + '"]');
    if (card) card.parentNode.removeChild(card);
    updatePageState();
  }

  // The card in a wishlist-card section response, or null.
  function cardFrom(html) {
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var card = tmp.querySelector('.grid__item[data-product-id]');
    // The theme starts AOS with its mutation observer off, so cards added
    // after load never get aos-animate and their images stay at opacity 0
    // (image-element fades in only under .aos-animate). Mark them the way
    // theme.js marks the cards it builds itself.
    if (card) {
      var aos = card.querySelectorAll('[data-aos]');
      for (var i = 0; i < aos.length; i++) aos[i].classList.add('aos-init', 'aos-animate');
      if (card.hasAttribute('data-aos')) card.classList.add('aos-init', 'aos-animate');
    }
    return card;
  }

  // One wishlist-card section per handle, in order, four requests at a time
  // (the store rate-limits bursts). Each result is { card, gone }: gone only
  // when the product answers 404 (deleted or unpublished). A network error,
  // 429, 5xx or a page without the card (a bot check) shows no card but keeps
  // the product saved.
  function fetchCards(handles) {
    var results = new Array(handles.length);
    var next = 0;
    function one(i) {
      return fetch(cfg.root + '/products/' + encodeURIComponent(handles[i]) + '?section_id=wishlist-card', {
        credentials: 'same-origin',
      })
        .then(function (res) {
          if (res.status === 404) return { card: null, gone: true };
          if (!res.ok) return { card: null, gone: false };
          return res.text().then(function (html) {
            return { card: cardFrom(html), gone: false };
          });
        })
        .catch(function () {
          return { card: null, gone: false };
        })
        .then(function (r) {
          results[i] = r;
        });
    }
    function worker() {
      if (next >= handles.length) return Promise.resolve();
      return one(next++).then(worker);
    }
    var workers = [];
    for (var w = 0; w < Math.min(4, handles.length); w++) workers.push(worker());
    return Promise.all(workers).then(function () {
      return results;
    });
  }

  // Loox draws stars once, for the ratings on the page when it ran, and has no
  // public re-scan. Cards added here get the same markup it writes, using
  // its icon sprite (#looxicons-rating-icon); without the sprite they keep
  // the plain "N Reviews" text, as before.
  function refreshRatings(root) {
    if (!document.getElementById('looxicons-rating-icon')) return;
    var els = root.querySelectorAll('.loox-rating:not([data-rating-upgraded])');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var rating = parseFloat(el.getAttribute('data-rating')) || 0;
      var raters = parseInt(el.getAttribute('data-raters'), 10) || 0;
      if (!raters) continue;
      var label = escapeHtml(raters + ' reviews');
      var html = '<div class="loox-rating-content">';
      for (var n = 1; n <= 5; n++) {
        var fill = rating >= n ? 'full' : rating >= n - 0.5 ? 'half' : 'empty';
        html +=
          '<svg class="loox-icon" viewBox="0 0 24 24" data-lx-fill="' + fill + '" aria-hidden="true">' +
          (n === 1 ? '<title>' + label + '</title>' : '') +
          '<use href="#looxicons-rating-icon"></use></svg>';
      }
      html += '<span class="loox-rating-label" aria-hidden="true">&nbsp;(' + raters + ')</span></div>';
      el.insertAdjacentHTML('beforeend', html);
      el.setAttribute('data-rating-upgraded', '1');
      el.setAttribute('role', 'figure');
      el.setAttribute('aria-label', label);
    }
  }

  function renderGuestPage() {
    var local = readLocal();
    if (!ownGrid || !local.length) {
      updatePageState();
      return;
    }
    page.classList.add('is-loading');
    fetchCards(
      local.map(function (x) {
        return x.h;
      })
    ).then(function (results) {
      var keep = [];
      results.forEach(function (r, i) {
        if (r.gone) return;
        if (r.card) ownGrid.appendChild(r.card);
        keep.push(local[i]);
      });
      // Products that no longer exist drop out of the saved list too; one that
      // failed to load this time stays saved.
      if (keep.length !== local.length) {
        writeLocal(keep);
        ids = keep.map(function (x) {
          return Number(x.id);
        });
      }
      page.classList.remove('is-loading');
      updatePageState();
      syncAll();
      refreshRatings(ownGrid);
    });
  }

  // Share: a link to this page carrying the product handles, newest first.
  function shareHandles() {
    var cards = ownCards();
    var handles = [];
    for (var i = 0; i < cards.length; i++) {
      var b = cards[i].querySelector('[data-wishlist-handle]');
      if (b) handles.push(b.getAttribute('data-wishlist-handle'));
    }
    return handles;
  }

  function share() {
    var handles = shareHandles();
    if (!handles.length) return;
    var url = location.origin + cfg.page + '?shared=' + handles.map(encodeURIComponent).join(',');
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'share_wishlist', wishlist_items: handles.length });
    var title = (page.querySelector('[data-wishlist-heading]') || {}).textContent || document.title;
    if (navigator.share) {
      navigator.share({ title: title.trim(), url: url }).catch(function () {}).then(function () {
        signInSheet('share');
      });
      return;
    }
    var copied = function () {
      toast(escapeHtml(cfg.text.link_copied));
      // The sheet follows once the "copied" note had its moment.
      if (!cfg.customer) setTimeout(function () {
        signInSheet('share');
      }, 1500);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(copied, function () {
        window.prompt('', url);
      });
    } else {
      window.prompt('', url);
    }
  }

  function sharedHandles() {
    var raw = new URLSearchParams(location.search).get('shared');
    if (!raw) return [];
    var seen = {};
    return raw
      .split(',')
      .map(function (h) {
        return h.trim();
      })
      .filter(function (h) {
        if (!h || h.length > 255 || /[\/?#\s]/.test(h) || seen[h]) return false;
        seen[h] = true;
        return true;
      })
      .slice(0, max);
  }

  function renderSharedPage(handles) {
    var grid = page.querySelector('[data-wishlist-shared-grid]');
    var heading = page.querySelector('[data-wishlist-heading]');
    var note = page.querySelector('[data-wishlist-shared-note]');
    var saveAll = page.querySelector('[data-wishlist-save-all]');
    page.classList.add('is-shared', 'is-loading');
    page.classList.remove('is-empty');
    if (heading && heading.getAttribute('data-shared-title')) heading.textContent = heading.getAttribute('data-shared-title');
    if (note) note.hidden = false;
    grid.hidden = false;

    fetchCards(handles).then(function (results) {
      var items = [];
      results.forEach(function (r) {
        var card = r.card;
        if (!card) return;
        grid.appendChild(card);
        var b = card.querySelector('[data-wishlist-btn]');
        if (b) items.push({ id: Number(b.getAttribute('data-wishlist-id')), h: b.getAttribute('data-wishlist-handle') });
      });
      page.classList.remove('is-loading');
      page.classList.toggle('is-empty', !items.length);
      syncAll();
      refreshRatings(grid);
      if (!saveAll || !items.length) return;
      saveAll.hidden = false;
      saveAll.addEventListener('click', function () {
        saveAll.disabled = true;
        addMany(items).then(function (ok) {
          saveAll.disabled = false;
          if (ok) {
            saveAll.hidden = true;
            if (cfg.customer) toast(escapeHtml(cfg.text.saved_all));
            else signInSheet('save_all');
          } else {
            toast(escapeHtml(cfg.text.error));
          }
        });
      });
    });
  }

  // Adds several products at once (shared list "save all"), newest first.
  function addMany(items) {
    var fresh = items.filter(function (x) {
      return !has(x.id);
    });
    if (!fresh.length) return Promise.resolve(true);
    if (!cfg.customer) {
      var local = readLocal();
      fresh
        .slice()
        .reverse()
        .forEach(function (x) {
          local = local.filter(function (y) {
            return Number(y.id) !== x.id;
          });
          local.unshift(x);
        });
      writeLocal(local);
      ids = local.map(function (x) {
        return Number(x.id);
      });
      syncAll();
      return Promise.resolve(true);
    }
    return proxy('/merge', {
      ids: fresh.map(function (x) {
        return x.id;
      }),
      source: 'save_all',
    }).then(
      function (data) {
        acceptServer(data.ids);
        return true;
      },
      function () {
        return false;
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Start

  syncAll();
  countSignInReturn();

  mergeGuestList().then(function (merged) {
    // The page was rendered before the merge; show the merged list.
    if (merged && page && !page.classList.contains('is-shared')) window.location.reload();
  });

  if (page) {
    var shared = sharedHandles();
    if (shared.length) {
      renderSharedPage(shared);
    } else if (!cfg.customer) {
      renderGuestPage();
    } else {
      updatePageState();
    }
    if (shareBtn) shareBtn.addEventListener('click', share);

    // The guest sign-in banner on the page itself (sections/main-wishlist).
    var pageSignIn = page.querySelector('[data-wishlist-signin]');
    if (!shared.length) stat('page_view', cfg.customer ? 'member' : 'guest', cfg.customer ? undefined : readLocal().length);
    if (pageSignIn && !cfg.customer && !shared.length) {
      pageSignIn.setAttribute('href', signInUrl('page'));
      stat('prompt_view', 'page');
      pageSignIn.addEventListener('click', function () {
        markSignIn('page');
      });
    }
  }

  if ('MutationObserver' in window) {
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        var fresh = document.querySelectorAll('[data-wishlist-btn]:not([data-wl-synced])');
        for (var i = 0; i < fresh.length; i++) syncButton(fresh[i]);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
